import React, {useState, useCallback} from 'react';
import {View, StyleSheet, SectionList, TouchableOpacity, ActivityIndicator} from 'react-native';
import {Text, Button, IconButton, Divider, Snackbar} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useFocusEffect} from '@react-navigation/native';
import {colors, spacing} from '../../theme';
import {paymentApi} from '../../services/api/paymentApi';
import PayWorkerModal from './PayWorkerModal';

const TypeBadge = ({type}) => {
  const isTask = type === 'Task';
  const bgColor = isTask ? colors.primary + '25' : '#FF980025';
  const textColor = isTask ? colors.primary : '#FF9800';
  return (
    <View style={[styles.badge, {backgroundColor: bgColor}]}>
      <Text style={[styles.badgeText, {color: textColor}]}>
        {isTask ? 'T' : 'D'}
      </Text>
    </View>
  );
};

const WorkerDuesScreen = () => {
  const {t} = useTranslation();
  const [dues, setDues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [snackbar, setSnackbar] = useState({visible: false, message: ''});
  const [payModal, setPayModal] = useState({visible: false, workerId: null, workerName: '', items: []});

  const fetchDues = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    try {
      const res = await paymentApi.getDues();
      setDues(res.data?.data || []);
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to load dues';
      setSnackbar({visible: true, message: msg});
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      fetchDues();
    }, [fetchDues]),
  );

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDues(false);
  };

  const sections = dues.map(group => ({
    title: group.workerName,
    workerTypeName: group.workerTypeName,
    workerId: group.workerId,
    total: group.totalDue,
    items: group.items,
    data: group.items,
  }));

  const formatDate = dateStr => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {month: 'short', day: 'numeric', year: 'numeric'});
  };

  const renderSectionHeader = ({section}) => (
    <View style={styles.sectionHeader}>
      <View style={styles.sectionInfo}>
        <Text style={styles.sectionWorkerName}>{section.title}</Text>
        {!!section.workerTypeName && (
          <Text style={styles.sectionWorkerType}>{section.workerTypeName}</Text>
        )}
      </View>
      <Button
        mode="contained"
        compact
        style={styles.payButton}
        labelStyle={styles.payButtonLabel}
        onPress={() =>
          setPayModal({
            visible: true,
            workerId: section.workerId,
            workerName: section.title,
            items: section.items,
          })
        }>
        Pay
      </Button>
      <View style={{flex: 1}} />
      <Text style={styles.sectionTotal}>{'\u20B9'}{section.total}</Text>
    </View>
  );

  const renderItem = ({item}) => (
    <View style={styles.dataRow}>
      <Text style={[styles.cell, styles.dateCell]}>{formatDate(item.completedAt)}</Text>
      <Text style={[styles.cell, styles.orderCell]}>{item.orderNumber || '-'}</Text>
      <View style={[styles.cell, styles.workedOnCell]}>
        <TypeBadge type={item.type} />
        <Text style={styles.workedOnText}>{item.taskTypeName}</Text>
      </View>
      <View style={[styles.cell, styles.amountCell]}>
        <Text style={styles.amountText}>{'\u20B9'}{item.amount || 0}</Text>
      </View>
    </View>
  );

  if (loading && dues.length === 0) {
    return (
      <View style={[styles.container, styles.centerContent]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Divider />

      <View style={styles.tableHeader}>
        <Text style={[styles.headerCell, styles.dateCell]}>Date</Text>
        <Text style={[styles.headerCell, styles.orderCell]}>Order#</Text>
        <Text style={[styles.headerCell, styles.workedOnCell]}>Worked On</Text>
        <Text style={[styles.headerCell, styles.amountCell]}>Amount</Text>
      </View>

      <SectionList
        sections={sections}
        keyExtractor={item => item.instanceTaskId ? `t-${item.instanceTaskId}` : `w-${item.workAssignmentId}`}
        renderSectionHeader={renderSectionHeader}
        renderItem={renderItem}
        stickySectionHeadersEnabled={false}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        ListEmptyComponent={
          <Text style={styles.emptyText}>{t('common.noData')}</Text>
        }
        ListFooterComponent={
          sections.length > 0 ? (
            <View style={styles.legend}>
              <View style={styles.legendItem}>
                <View style={[styles.badge, {backgroundColor: colors.primary + '25'}]}>
                  <Text style={[styles.badgeText, {color: colors.primary}]}>T</Text>
                </View>
                <Text style={styles.legendText}>Task</Text>
              </View>
              <View style={styles.legendItem}>
                <View style={[styles.badge, {backgroundColor: '#FF980025'}]}>
                  <Text style={[styles.badgeText, {color: '#FF9800'}]}>D</Text>
                </View>
                <Text style={styles.legendText}>Dress Assignment</Text>
              </View>
            </View>
          ) : null
        }
      />

      {payModal.visible && (
        <PayWorkerModal
          visible={payModal.visible}
          onDismiss={() => setPayModal({visible: false, workerId: null, workerName: '', items: []})}
          workerName={payModal.workerName}
          workerId={payModal.workerId}
          items={payModal.items}
          onPaymentComplete={() => fetchDues(false)}
        />
      )}

      <Snackbar
        visible={snackbar.visible}
        onDismiss={() => setSnackbar({visible: false, message: ''})}
        duration={3000}>
        {snackbar.message}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.surface},
  centerContent: {justifyContent: 'center', alignItems: 'center'},
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.primary,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  headerCell: {
    fontSize: 13,
    fontWeight: '700',
    color: colors.white,
  },
  dateCell: {flex: 2},
  orderCell: {flex: 2, textAlign: 'center'},
  workedOnCell: {flex: 2.5, flexDirection: 'row', alignItems: 'center'},
  amountCell: {flex: 2, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end'},
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.background,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
  },
  sectionInfo: {flexDirection: 'column'},
  sectionWorkerName: {fontSize: 15, fontWeight: '700', color: colors.text},
  sectionWorkerType: {fontSize: 11, color: colors.textSecondary, marginTop: 1},
  payButton: {
    marginLeft: spacing.sm,
    backgroundColor: colors.textSecondary,
    borderRadius: 16,
    height: 28,
    justifyContent: 'center',
  },
  payButtonLabel: {fontSize: 12, marginVertical: 0, marginHorizontal: spacing.sm},
  sectionTotal: {fontSize: 15, fontWeight: '700', color: colors.text},
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: colors.divider,
  },
  cell: {fontSize: 13, color: colors.text},
  workedOnText: {fontSize: 13, color: colors.text, marginLeft: spacing.xs},
  amountText: {fontSize: 13, color: colors.error, fontWeight: '600'},
  badge: {
    width: 22,
    height: 22,
    borderRadius: 11,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {fontSize: 11, fontWeight: '700'},
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
    fontSize: 16,
  },
  legend: {
    flexDirection: 'row',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    gap: spacing.md,
  },
  legendItem: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  legendText: {fontSize: 13, color: colors.text},
});

export default WorkerDuesScreen;
